import React, { useState, useContext, useMemo, useEffect } from "react";
import { GlobalStates } from "../GlobalStates";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress } from "@mui/material";
import { supabase } from "../services/supabaseClient";

import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useLocalStore } from "../Hooks/useLocalStore";
import { getTabStorage, setTabStorage } from "../utils/tabStorage";
import { useUrlParams } from "../Hooks/useUrlParams";
import useGlobalData from "../Hooks/useGlobalData";

import RezultataiTable from "../Components/RezultataiTable";
import { FiltruChipai } from "../Components/FiltruChipai";

import { useFilteredData, useYearManagement } from "./Rezultatai/hooks";
import { Filters, ActionButtons } from "./Rezultatai/components";
import { translateFormName } from "../utils/translateFormName";

const convertToStrapiFormat = (item) => {
  // Atvirkštinis fieldMap
  const reverseFieldMap = {
    formos_pavadinimas: "formosPavadinimas",
    imones_pavadinimas: "imonesPavadinimas",
    form_object: "formObject",
    kas_pildo: "kasPildo",
    valid_to: "validTo",
    created_at: "createdAt",
    updated_at: "updatedAt",
    uzpildymo_data: "uzpildymoData",
  };

  // Optimizuota transformacija
  return {
    id: item.id,
    attributes: Object.entries(item).reduce((acc, [key, value]) => {
      if (key === "id") return acc;
      const newKey = reverseFieldMap[key] || key;
      acc[newKey] = value;
      return acc;
    }, {}),
  };
};

const Rezultatai = () => {
  const { user, userStatus } = useContext(GlobalStates); // Get userStatus, remove type
  const { t } = useTranslation();
  const location = useLocation();
  const { params: filters, setParam } = useUrlParams();
  const { selectedCompany, setSelectedCompany } = useGlobalData();
  const queryClient = useQueryClient();

  // Local state
  const [drawer, setDrawer] = useState(false);

  // Define isUserAdmin in the main component scope using useMemo
  const isUserAdmin = useMemo(
    () => userStatus?.admin_confirmed === true,
    [userStatus]
  );

  // Get local store data first
  const { data: tipas } = useLocalStore('tipas');
  const { data: klausimas, setData: setKlausimas } = useLocalStore("klausimas");

  // Pagerintas Accordion valdymas, kai paspaudžiamas Accordion
  const handleAccordionChange = () => {
    // Išvalom klausimo būseną, kai keliame naują kortelę
    setKlausimas(null);
  };
  
  // Simple local filter state with global company sync
  const [filtersMain, setFilterMain] = useState({
    kasPildo: tipas || "Darbuotojo forma",
    imonesPavadinimas: selectedCompany,
  });

  const [currentYear, setYear] = useState(() => {
    const savedYear = getTabStorage("currentYear");
    const dateYear = new Date().getFullYear();

    return savedYear !== null ? parseInt(savedYear) : dateYear;
  });

  const { data: visosImones, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ["companies-rezultatai", filtersMain.kasPildo],
    queryFn: async () => {
      const { data } = await supabase
        .from("uzpildytos_imones")
        .select("imones_pavadinimas")
        .contains("kas_pilde", [filtersMain.kasPildo]);

      return data.map(({ imones_pavadinimas }) => imones_pavadinimas);
    },
    enabled: !!filtersMain.kasPildo,
    staleTime: 10 * 60 * 1000, // 10 minutes - companies change infrequently
    cacheTime: 15 * 60 * 1000, // 15 minutes
  });

  // Simplified sync - only update local filters when global state changes
  useEffect(() => {
    if (selectedCompany && selectedCompany !== filtersMain.imonesPavadinimas) {
      setFilterMain(prev => ({
        ...prev,
        imonesPavadinimas: selectedCompany
      }));
    }
  }, [selectedCompany, setFilterMain]);

  // Sync local to global only when setting a specific company
  useEffect(() => {
    if (filtersMain.imonesPavadinimas && filtersMain.imonesPavadinimas !== selectedCompany) {
      setSelectedCompany(filtersMain.imonesPavadinimas);
    }
  }, [filtersMain.imonesPavadinimas, selectedCompany, setSelectedCompany]);

  useEffect(() => {
    const locationFilters = location?.state?.filter || {};

    // Always use the form type from location if available, otherwise use saved or default
    const formType =
    filtersMain.kasPildo || locationFilters.kasPildo || "Darbuotojo forma";

    // Start with the current selected company if one exists
    const currentCompany = selectedCompany || filtersMain.imonesPavadinimas;

    // For all users, respect the form type selection while maintaining company restrictions for non-admin
    const baseFilters = {
      kasPildo: formType,
    };

    // For non-admin users, always include their company filter
    if (!isUserAdmin) {
      const userCompany =
        user?.user_metadata?.imones_pavadinimas || user?.imonesPavadinimas;
      if (userCompany) {
        baseFilters.imonesPavadinimas = userCompany;
        // Also sync this to global state for non-admin users
        if (userCompany !== selectedCompany) {
          setSelectedCompany(userCompany);
        }
      }
    }
    // For admin users, preserve their company selection from global state
    else if (currentCompany) {
      baseFilters.imonesPavadinimas = currentCompany;
    }

    // Only auto-select company if no company is already selected and there's only one option
    if (
      !isLoadingCompanies &&
      !baseFilters.imonesPavadinimas &&
      visosImones?.length === 1
    ) {
      baseFilters.imonesPavadinimas = visosImones[0];
      // Sync auto-selected company to global state
      if (visosImones[0] !== selectedCompany) {
        setSelectedCompany(visosImones[0]);
      }
    }

    // Only update the filter if something actually changed
    if (JSON.stringify(filtersMain) !== JSON.stringify(baseFilters)) {
      setFilterMain(baseFilters);
    }
  }, [
    // Only trigger this effect when these specific dependencies change
    isUserAdmin,
    location?.state?.filter,
    user,
    selectedCompany,
    visosImones,
    isLoadingCompanies,
    setSelectedCompany
    // Don't include filtersMain to prevent reset loops
  ]);


  const { data: yearOptions = [currentYear] } = useQuery({
    queryKey: ["years"],
    queryFn: async () => {
      const { data } = await supabase
        .rpc("get_distinct_years")
        .order("year_created", { ascending: false });

      return data?.map(({ year_created }) => Number(year_created));
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - years change very infrequently
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });

  const { data: atsakymai, isFetching: isLoadingAnswers } = useQuery({
    queryKey: [
      "answers",
      currentYear,
      filtersMain.kasPildo,
      filtersMain.imonesPavadinimas,
    ],
    queryFn: async () => {
      const query = supabase
        .from("atsakymai")
        .select("*")
        .eq("yearCreated", currentYear)
        .eq("kas_pildo", filtersMain.kasPildo);

      if (filtersMain.imonesPavadinimas) {
        query.eq("imones_pavadinimas", filtersMain.imonesPavadinimas);
      }
      const { data } = await query;

      return data.map(convertToStrapiFormat);
    },
    enabled:
      !!filtersMain.kasPildo &&
      !!currentYear &&
      !(!isUserAdmin && !filtersMain.imonesPavadinimas),
    staleTime: 3 * 60 * 1000, // 3 minutes - answers change more frequently
    cacheTime: 8 * 60 * 1000, // 8 minutes
  });

  const { data: tipai, isFetching: isLoadingForms } = useQuery({
    queryKey: [
      "forms",
      currentYear,
      filtersMain.kasPildo,
      filtersMain.imonesPavadinimas,
    ],
    queryFn: async () => {
      const query = supabase
        .from("formos")
        .select("*")
        .eq("yearCreated", currentYear)
        .eq("kas_pildo", filtersMain.kasPildo);

      if (filtersMain.imonesPavadinimas) {
        query.eq("imones_pavadinimas", filtersMain.imonesPavadinimas);
      }
      const { data } = await query;

      return data.map(convertToStrapiFormat);
    },
    enabled:
      !!filtersMain.kasPildo &&
      !!currentYear &&
      !(!isUserAdmin && !filtersMain.imonesPavadinimas),
    staleTime: 8 * 60 * 1000, // 8 minutes - forms change less frequently than answers
    cacheTime: 12 * 60 * 1000, // 12 minutes
  });

  const { tvarkyta, tvarkytaTipai } = useFilteredData(
    tipai,
    atsakymai,
    filtersMain,
    filters,
    currentYear
  );

  useYearManagement(yearOptions, currentYear, setYear);

  // Loading state - define this before using it
  const isLoadingState = useMemo(
    () => isLoadingAnswers || isLoadingForms || isLoadingCompanies,
    [isLoadingAnswers, isLoadingCompanies, isLoadingForms]
  );

  // Prefetch data for Tendencijos page to improve navigation
  useEffect(() => {
    if (filtersMain.kasPildo && !isLoadingState) {
      // Prefetch companies for Tendencijos using correct query key and source
      queryClient.prefetchQuery({
        queryKey: ["companies-tendencijos", filtersMain.kasPildo],
        queryFn: async () => {
          return supabase
            .from('atsakymai')
            .select('imones_pavadinimas')
            .eq('kas_pildo', filtersMain.kasPildo)
            .throwOnError()
            .order('imones_pavadinimas');
        },
        staleTime: 10 * 60 * 1000,
      });

      // Prefetch forms for Tendencijos  
      queryClient.prefetchQuery({
        queryKey: ["forms-tendencijos", filtersMain.kasPildo],
        queryFn: async () => {
          const { data: tipaiData } = await supabase
            .from('formos')
            .select('*')
            .eq('kas_pildo', filtersMain.kasPildo);
          
          return tipaiData?.map(item => ({
            id: item.id,
            attributes: {
              kasPildo: item.kas_pildo,
              formObject: item.form_object,
              formosPavadinimas: item.formos_pavadinimas,
              imonesPavadinimas: item.imones_pavadinimas,
            }
          })) || [];
        },
        staleTime: 8 * 60 * 1000,
      });
    }
  }, [filtersMain.kasPildo, isLoadingState, queryClient]);

  const atskleistas = useMemo(() => {
    if (!klausimas || !tvarkytaTipai || !tvarkytaTipai[0]) return -1;

    // Saugesnis tikrinimas ar yra reikiamos savybės
    const formObject = tvarkytaTipai[0]?.attributes?.formObject;
    if (!formObject || !formObject.pages || !Array.isArray(formObject.pages))
      return -1;

    return formObject.pages.findIndex(({ elements }) =>
      elements?.some(({ name }) => name?.includes(klausimas))
    ) - 1;
  }, [klausimas, tvarkytaTipai]);

  // useEffect(() => {
  //   // Saugesnis būdas nustatyti aktyvaus elemento indeksą
  //   if (atskleistas !== -1 && !isNil(atskleistas)) {
  //     // Atidedame indekso pakeitimą, kad išvengtume kontroliuojamų/nekontroliuojamų komponentų konfliktų
  //     const timeoutId = setTimeout(() => {
  //       // Galima pridėti logiką čia, jei reikia
  //     }, 50);
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [atskleistas]);

  const isDarbuotojoForma = useMemo(() => 
    filtersMain?.kasPildo === "Darbuotojo forma", 
    [filtersMain?.kasPildo]
  );

  // Memoize filter props to prevent unnecessary re-renders
  const memoizedFilterProps = useMemo(() => ({
    visosImones,
    filters,
    years: yearOptions,
    setYear,
    currentYear,
    setFilter: setParam,
    setFilterMain,
    filtersMain,
    user,
    isLoading: isLoadingState,
    isAdmin: isUserAdmin
  }), [visosImones, filters, yearOptions, setYear, currentYear, setParam, setFilterMain, filtersMain, user, isLoadingState, isUserAdmin]);

  // Memoize action button props - ensure tvarkytaTipai and tvarkyta are available
  const memoizedActionProps = useMemo(() => ({
    isfiltruotiTipai: tvarkytaTipai || [],
    tvarkyta: tvarkyta || [],
    drawer,
    setDrawer,
    setFilter: setParam,
    formType: filtersMain.kasPildo,
    tipai: currentYear && tvarkytaTipai?.length > 0 ? tvarkytaTipai : (tipai || []),
    atsakymai: tvarkyta || []
  }), [tvarkytaTipai, tvarkyta, drawer, setDrawer, setParam, filtersMain.kasPildo, currentYear, tipai]);

  return (
    <div className="tendencijosCont stickyParent">
      <div className="standartPadding">
        <p className="rezTenName accorcionName">{t("rezultataiRezultatai")}</p>
      </div>

      <div className="standartPadding sticky">
        <div className="row spaceBetween sticky">
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Filters {...memoizedFilterProps} />
          </div>

          <ActionButtons {...memoizedActionProps} />
        </div>
        <FiltruChipai />
      </div>

      <div className="standartPadding bgBlue tendRezMargin rezBotSpace">
        {tvarkytaTipai.length > 0 && (
          <div className="column borderBot">
            <h4 className="metai">{currentYear || ""}</h4>
            <Box
              sx={{ display: "flex" }}
              flexDirection={"row"}
              justifyContent="space-between"
              alignItems="center"
            >
              <h2 className="apklausosPavadinimas">
                {translateFormName(
                  tvarkytaTipai[0]?.attributes.formosPavadinimas ||
                    tvarkytaTipai[0]?.attributes.formos_pavadinimas,
                  t
                )}
              </h2>
              {isDarbuotojoForma && (
                <Typography fontSize={14}>
                  {t("atliko", { kiek: tvarkyta.length })}
                </Typography>
              )}
            </Box>
          </div>
        )}

        {isLoadingState && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
            {/* Corrected loading message check */}
            {!isUserAdmin &&
              !user?.user_metadata?.imones_pavadinimas &&
              !user?.imonesPavadinimas && (
                <Typography sx={{ mt: 2 }}>{t("loading_user_data")}</Typography>
              )}
          </Box>
        )}

        {!isLoadingState && (
          <div
            className="rezTableContainer"
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <RezultataiTable
              handleOpen={handleAccordionChange}
              tipai={tvarkytaTipai}
              info={tvarkyta}
              pilnaInfo={atsakymai}
              filtrai={filters}
              metai={currentYear}
              atskleistas={atskleistas > -1 ? atskleistas : null}
            />
          </div>
        )}

        {!isLoadingState &&
        (tvarkytaTipai.length === 0 || tvarkyta.length === 0) ? (
          <div className="neraAts">
            <div>{t("Nera_atsakymu")}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Rezultatai;
